import { Typography, type TypographyProps } from "@mui/material"
import { useLayoutEffect, useRef, useState } from "react"

interface FitOneLineTextProps extends TypographyProps {
  text: string
}

/**
 * Renders `text` as MUI Typography that never wraps — if it would overflow its
 * container, it's scaled down proportionally to fit on one line instead.
 */
const FitOneLineText = ({ text, sx, ...typographyProps }: FitOneLineTextProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const textRef = useRef<HTMLElement>(null)
  const [scale, setScale] = useState(1)

  useLayoutEffect(() => {
    const container = containerRef.current
    const el = textRef.current
    if (!container || !el) return

    const measure = () => {
      const containerWidth = container.clientWidth
      const textWidth = el.scrollWidth
      if (containerWidth <= 0 || textWidth <= 0) return
      setScale(Math.min(1, containerWidth / textWidth))
    }

    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(container)

    return () => observer.disconnect()
  }, [text])

  return (
    <div ref={containerRef} style={{ minWidth: 0, overflow: "hidden" }}>
      <Typography
        ref={textRef}
        sx={{
          ...sx,
          display: "inline-block",
          whiteSpace: "nowrap",
          transform: `scale(${scale})`,
          transformOrigin: "left center",
        }}
        {...typographyProps}
      >
        {text}
      </Typography>
    </div>
  )
}

export default FitOneLineText
